import { useCallback } from "react";
import {
  MultiUploadedFileResponse,
  PhotogrammetryUploadTaskContext,
  UploadElementType,
  UploadMultipleFilesParams,
} from "@custom-types/file-upload-types";
import { SdbProject } from "@custom-types/project-types";
import { PhotogrammetryFilesInfo } from "@pages/data-management/data-management-types";
import { UploadFilesErrorToastType, useUploadFilesTrackError } from "@hooks/data-management/use-upload-files-track-error";
import { useUploadFilesTrackFinish } from "@hooks/data-management/use-upload-files-track-finish";
import { useUploadFilesTrackStart } from "@hooks/data-management/use-upload-files-track-start";
import { useSendMutation } from "@pages/data-management/photogrammetry-data/photogrammetry-data-mutation";
import { assert } from "@faro-lotv/foundation";
import { isPhotogrammetryUploadTaskContext } from "@custom-types/file-upload-type-guards";
import { useFileUpload } from "@hooks/use-file-upload";
import { UploadSettings } from "@hooks/data-management/use-upload-multiple-scans";

type ReturnFunction = (
  /** Data provided by the associated file validation helper getPhotogrammetryFilesInfo. */
  fileInfos: PhotogrammetryFilesInfo,
  uploadSettings?: UploadSettings,
) => Promise<boolean>;

interface UploadResult {
  uploadedResponse: MultiUploadedFileResponse;
  /** The context of the file upload task. */
  context: PhotogrammetryUploadTaskContext;
}

/**
 * Uploads the image files in the info object provided by useOnCheckFiles / getPhotogrammetryFilesInfo
 * for photogrammetry processing, and triggers a photogrammetry task after a successful upload.
 * @param project The project to upload files to.
 */
export function useOnUploadPhotogrammetryFiles(project: SdbProject): ReturnFunction {
  const uploadFilesTrackStart = useUploadFilesTrackStart();
  const uploadFilesTrackError = useUploadFilesTrackError();
  const uploadFilesTrackFinish = useUploadFilesTrackFinish();
  const { uploadMultipleFiles } = useFileUpload();
  const sendMutation = useSendMutation(project);

  return useCallback(
    (info, uploadSettings): Promise<boolean> => {
      const { context, filesToUpload, name } = info;
      const elementType = UploadElementType.photogrammetry;

      async function uploadFiles(): Promise<UploadResult> {
        return new Promise<UploadResult>((resolve, reject) => {
          const uploadParams: UploadMultipleFilesParams = {
            files: filesToUpload,
            onUploadStart: () => undefined,
            onUploadProgress: () => undefined,
            onUploadComplete: (uploadedResponse, context) => {
              assert(isPhotogrammetryUploadTaskContext(context));
              resolve({ uploadedResponse, context });
            },
            context,
          };

          uploadMultipleFiles(uploadParams).catch((error) => {
            reject(error);
          });
        });
      }

      async function onUploadComplete(result: UploadResult): Promise<void> {
        await uploadFilesTrackFinish(result.uploadedResponse, elementType);

        // Like for Blink uploads, any failed upload will abort the operation.
        const failedUploads = result.uploadedResponse.failed;
        if (failedUploads.length > 0) {
          uploadFilesTrackError(UploadFilesErrorToastType.uploadPhotogrammetryFiles, elementType);
          return;
        }

        try {
          // Trigger the mutation to add the photogrammetry input data to the project, then trigger the photogrammetry task.
          await sendMutation({
            selectedFiles: filesToUpload,
            uploadedResponse: result.uploadedResponse,
            scanId: context.scanId,
            scanName: name,
          });
        } catch {
          uploadFilesTrackError(UploadFilesErrorToastType.sendMutation, elementType);
        }
      }

      // Return if there is no uploadable file (unexpected).
      // Resolve to true so that it's consistent with the other file types that call useUploadMultipleScans, which
      // returns immediately, and then return true. This also seems semantically okay since the file validation has
      // already handled an entirely empty file selection, and an empty upload of a specific file type isn't a problem
      // per se.
      if (!filesToUpload.length) {
        return Promise.resolve(true);
      }

      uploadFilesTrackStart({}, filesToUpload, elementType, uploadSettings);

      try {
        // We cannot await uploadFiles() here, otherwise the UI would not show the upload progress.
        uploadFiles()
          .then(onUploadComplete)
          .catch((_error) => {
            uploadFilesTrackError(UploadFilesErrorToastType.uploadPhotogrammetryFiles, elementType);
          });
        return Promise.resolve(true);
      } catch {
        // The upload failed to start.
        uploadFilesTrackError(UploadFilesErrorToastType.uploadPhotogrammetryFiles, elementType);
        return Promise.resolve(false);
      }
    },
    [
      sendMutation,
      uploadFilesTrackError,
      uploadFilesTrackFinish,
      uploadFilesTrackStart,
      uploadMultipleFiles,
    ]
  );
}
